import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import api from '../../axiosInstance';
import { apiUrl } from '../../constants';

const AdminImportProjects = () => {
  const role = useSelector((state) => state.auth.role);
  const isSuperuser = role === 'superuser';

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');

  const accept = useMemo(
    () =>
      [
        '.csv',
        '.xlsx',
        '.xls',
        'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
      ].join(','),
    []
  );

  if (!isSuperuser) {
    return <Navigate to="/home" replace />;
  }

  const onPickFile = (e) => {
    const picked = e.target.files?.[0] || null;
    setFile(picked);
    setResult(null);
    setErrorMessage('');
  };

  const onUpload = async () => {
    if (!file) {
      setErrorMessage('Please choose a file first.');
      return;
    }

    setUploading(true);
    setErrorMessage('');
    setResult(null);

    try {
      const form = new FormData();
      form.append('file', file);

      const response = await api.post(apiUrl.ADMIN_IMPORT_PROJECT_INFO_URL, form, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setResult(response.data);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Upload failed';
      setErrorMessage(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700, mb: 2 }}>
        Admin: Import Project Info
      </Typography>

      <Paper sx={{ p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
        <Typography sx={{ color: '#B2EBF2', mb: 1 }}>
          Upload a CSV/XLSX template with columns: Team, Subteam, Location, Project, Year, Start Date, End Date.
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
            Choose file
            <input hidden type="file" accept={accept} onChange={onPickFile} />
          </Button>

          <Typography sx={{ color: '#FFFFFF' }}>
            {file ? file.name : 'No file selected'}
          </Typography>

          <Button
            variant="contained"
            color="success"
            onClick={onUpload}
            disabled={!file || uploading}
          >
            {uploading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Uploading...
              </Box>
            ) : (
              'Upload & Import'
            )}
          </Button>
        </Box>

        {errorMessage ? (
          <Typography sx={{ color: '#ffb4b4', mt: 2, whiteSpace: 'pre-wrap' }}>
            {errorMessage}
          </Typography>
        ) : null}
      </Paper>

      {result?.summary ? (
        <Paper sx={{ mt: 3, p: 2, backgroundColor: 'rgba(0,0,0,0.2)' }}>
          <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
            Summary
          </Typography>
          <Typography sx={{ color: '#FFFFFF' }}>
            Total rows: {result.summary.totalRows} | Inserted: {result.summary.inserted} | Updated:{' '}
            {result.summary.updated} | Skipped: {result.summary.skipped}
          </Typography>

          {typeof result.summary.updatedDuplicateInFile === 'number' ||
          typeof result.summary.updatedExistingInDb === 'number' ? (
            <Typography sx={{ color: '#B2EBF2', mt: 0.5 }}>
              Updated breakdown:{' '}
              {typeof result.summary.updatedExistingInDb === 'number'
                ? `existing-in-DB ${result.summary.updatedExistingInDb}`
                : 'existing-in-DB ?'}
              {' | '}
              {typeof result.summary.updatedDuplicateInFile === 'number'
                ? `duplicate-in-file ${result.summary.updatedDuplicateInFile}`
                : 'duplicate-in-file ?'}
            </Typography>
          ) : null}

          {result.summary.sheetName ? (
            <Typography sx={{ color: '#B2EBF2', mt: 0.5 }}>
              Imported sheet: {result.summary.sheetName}
              {result.summary.sheetRef ? ` (${result.summary.sheetRef})` : ''}
            </Typography>
          ) : null}

          {Array.isArray(result.errors) && result.errors.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ color: '#FFD700', mb: 1 }}>
                Errors / Skipped rows
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#B2EBF2' }}>Row</TableCell>
                    <TableCell sx={{ color: '#B2EBF2' }}>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.errors.slice(0, 100).map((e, idx) => (
                    <TableRow key={idx}>
                      <TableCell sx={{ color: '#FFFFFF' }}>{e.rowIndex}</TableCell>
                      <TableCell sx={{ color: '#FFFFFF', whiteSpace: 'pre-wrap' }}>{e.error}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {result.errors.length > 100 ? (
                <Typography sx={{ color: '#B2EBF2', mt: 1 }}>
                  Showing first 100 errors.
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </Paper>
      ) : null}
    </Box>
  );
};

export default AdminImportProjects;
